import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { bulkTransferApi } from '../services/trungTamApi';
import type { TtCourse } from '../types';

type Props = {
  open: boolean;
  onClose: () => void;
  onTransferred: () => void;
  selectedIds: Set<number>;
  courses: TtCourse[];
  currentCourseId: number;
};

const TransferRecordsModal: React.FC<Props> = ({
  open, onClose, onTransferred, selectedIds, courses, currentCourseId,
}) => {
  const [targetId, setTargetId] = useState<number | null>(null);
  const [transferring, setTransferring] = useState(false);

  if (!open) return null;

  // Only same-hang open courses are eligible — matches backend rule.
  // Source hang comes from the currently-selected course (records in
  // that course inherit its hang); cross-hang transfers are rejected
  // server-side anyway so filtering here avoids 409 round-trips.
  const currentCourse = courses.find(c => c.id === currentCourseId);
  const sourceHang = currentCourse?.hang;
  const openCourses = courses.filter(c =>
    c.status === 'open'
    && c.id !== currentCourseId
    && (sourceHang ? c.hang === sourceHang : true),
  );

  const handleTransfer = async () => {
    if (!targetId) {
      toast.warn('Chọn khóa học đích');
      return;
    }
    setTransferring(true);
    try {
      const resp = await bulkTransferApi(Array.from(selectedIds), targetId);
      if (resp.EC === 0) {
        toast.success(resp.EM);
        onTransferred();
        onClose();
      } else {
        toast.error(resp.EM || 'Lỗi chuyển khóa');
      }
    } catch {
      toast.error('Lỗi kết nối server');
    } finally {
      setTransferring(false);
    }
  };

  return createPortal(
    <div className="tt-transfer-modal__overlay" onClick={onClose}>
      <div className="tt-transfer-modal" onClick={e => e.stopPropagation()}>
        <div className="tt-transfer-modal__header">
          <h3>
            <i className="material-icons">drive_file_move</i>
            Chuyển {selectedIds.size} hồ sơ sang khóa khác
          </h3>
          <button type="button" className="tt-transfer-modal__close" onClick={onClose}>
            <i className="material-icons">close</i>
          </button>
        </div>

        <div className="tt-transfer-modal__body">
          {openCourses.length === 0 ? (
            <p className="tt-transfer-modal__empty">
              <i className="material-icons">info</i>
              {sourceHang
                ? `Không có khóa nào đang mở cùng hạng ${sourceHang} để chuyển sang.`
                : 'Không có khóa học nào đang mở để chuyển.'}
            </p>
          ) : (
            <>
              <p className="tt-transfer-modal__hint">
                Chọn khóa đích (đang mở{sourceHang ? `, cùng hạng ${sourceHang}` : ''}):
              </p>
              <div className="tt-transfer-modal__list">
                {openCourses.map(c => (
                  <label
                    key={c.id}
                    className={`tt-transfer-modal__option ${targetId === c.id ? 'tt-transfer-modal__option--selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="targetCourse"
                      value={c.id}
                      checked={targetId === c.id}
                      onChange={() => setTargetId(c.id)}
                    />
                    <div className="tt-transfer-modal__option-info">
                      <span className="tt-transfer-modal__option-name">{c.name}</span>
                      <span className="tt-transfer-modal__option-meta">
                        Hạng {c.hang} &middot; {c.recordCount} hồ sơ
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="tt-transfer-modal__footer">
          <button
            type="button"
            className="tt-btn tt-btn--outline"
            onClick={onClose}
            disabled={transferring}
          >
            Hủy
          </button>
          <button
            type="button"
            className="tt-btn tt-btn--primary"
            onClick={handleTransfer}
            disabled={transferring || !targetId || openCourses.length === 0}
          >
            {transferring ? (
              <span className="tt-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
            ) : (
              <i className="material-icons">drive_file_move</i>
            )}
            Chuyển khóa
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TransferRecordsModal;
